import { AreaChart, Area, XAxis, CartesianGrid, ResponsiveContainer, Legend } from 'recharts';
import { useEffect, useRef, useState } from 'react';

const projectionData = [
  { month: 'Day 1', streams: 800, fans: 1200, socials: 400 },
  { month: 'Week 1', streams: 1800, fans: 2400, socials: 1500 },
  { month: 'Week 2', streams: 2800, fans: 4200, socials: 3600 },
  { month: 'Week 3', streams: 4500, fans: 7500, socials: 4000 },
  { month: 'Month 1', streams: 7200, fans: 9500, socials: 5800 },
  { month: 'Week 5', streams: 10500, fans: 11000, socials: 8500 },
  { month: 'Week 6', streams: 14500, fans: 12800, socials: 11200 },
  { month: 'Week 7', streams: 17000, fans: 14500, socials: 16000 },
  { month: 'Month 2', streams: 20500, fans: 16500, socials: 19000 },
  { month: 'Week 9', streams: 23000, fans: 19000, socials: 24500 },
  { month: 'Week 10', streams: 27000, fans: 22500, socials: 28000 },
  { month: 'Month 3', streams: 30000, fans: 26000, socials: 35000 }
];

interface GrowthProjectionChartProps {
  className?: string;
}

export function GrowthProjectionChart({ className = '' }: GrowthProjectionChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [pulsePhase, setPulsePhase] = useState(0);

  useEffect(() => {
    // Check for reduced motion preference
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.2 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Pulsing animation for chart lines
  useEffect(() => {
    if (prefersReducedMotion || !isVisible) return;

    const interval = setInterval(() => {
      setPulsePhase((prev) => (prev + 6) % 360);
    }, 40); // Update every 40ms for smooth animation

    return () => clearInterval(interval);
  }, [prefersReducedMotion, isVisible]);

  // Calculate pulsing opacity (0.5 to 1.0)
  const getOpacity = (offset: number) => {
    const phase = (pulsePhase + offset) % 360;
    const radians = (phase * Math.PI) / 180;
    return 0.5 + 0.5 * (Math.sin(radians) * 0.5 + 0.5);
  };

  return (
      <div 
        ref={containerRef}
        className={`relative overflow-hidden bg-black/15 border border-white/10 rounded-2xl p-5 shadow-[0_8px_32px_rgba(0,0,0,0.2),_0_0_18px_rgba(0,113,227,0.18)] mx-auto ${className}`}
      >
        <ResponsiveContainer width="100%" height={280}>
        <AreaChart
          data={projectionData}
          margin={{ top: 10, right: 10, left: 10, bottom: 20 }}
        >
          <Legend 
            verticalAlign="top" 
            height={36}
            iconType="line"
            wrapperStyle={{
              paddingBottom: '8px',
              fontSize: '12px',
              color: 'rgba(255,255,255,0.9)'
            }}
          />
          <defs>
            <linearGradient id="streamsGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#00ff88" stopOpacity={0.5}/>
              <stop offset="95%" stopColor="#00ff88" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="fansGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#00d4ff" stopOpacity={0.5}/>
              <stop offset="95%" stopColor="#00d4ff" stopOpacity={0}/>
            </linearGradient>
            <linearGradient id="socialsGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#ff0080" stopOpacity={0.5}/>
              <stop offset="95%" stopColor="#ff0080" stopOpacity={0}/>
            </linearGradient>
          </defs>
          
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
          
          <XAxis 
            dataKey="month" 
            stroke="rgba(255,255,255,0.3)"
            tick={{ fontSize: 11, fill: 'rgba(255,255,255,0.5)' }}
            tickFormatter={(value) => {
              // Show Day 1, Month 1, Month 2, Month 3
              if (value === 'Day 1' || value.startsWith('Month')) return value;
              return '';
            }}
          />
          
          <Area
            type="monotone" 
            dataKey="streams" 
            stroke="#00ff88" 
            strokeWidth={3}
            strokeOpacity={prefersReducedMotion ? 1 : getOpacity(0)}
            fillOpacity={prefersReducedMotion ? 1 : getOpacity(0)}
            fill="url(#streamsGradient)" 
            name="Streams"
            animationDuration={isVisible ? 1500 : 0}
            style={{
              filter: 'drop-shadow(0 0 8px rgba(0, 255, 136, 0.6))'
            }}
          />
          
          <Area 
            type="monotone" 
            dataKey="fans" 
            stroke="#00d4ff" 
            strokeWidth={3}
            strokeOpacity={prefersReducedMotion ? 1 : getOpacity(120)}
            fillOpacity={prefersReducedMotion ? 1 : getOpacity(120)}
            fill="url(#fansGradient)" 
            name="Fans"
            animationDuration={isVisible ? 1500 : 0}
            style={{
              filter: 'drop-shadow(0 0 8px rgba(0, 212, 255, 0.6))'
            }}
          />
          
          <Area 
            type="monotone" 
            dataKey="socials" 
            stroke="#ff0080" 
            strokeWidth={3}
            strokeOpacity={prefersReducedMotion ? 1 : getOpacity(240)}
            fillOpacity={prefersReducedMotion ? 1 : getOpacity(240)}
            fill="url(#socialsGradient)" 
            name="Socials"
            animationDuration={isVisible ? 1500 : 0}
            style={{
              filter: 'drop-shadow(0 0 8px rgba(255, 0, 128, 0.6))'
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}